/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { Fragment, useState } from 'react';
import { Button, FormGroup, Input, Label } from 'reactstrap';
import Loader from '../loader';
import { apiClient } from '../../actions/api';
import { FilterOption } from '../settings-sidebar';
import { getSDGOptions } from '../survey/survey-question-list-toolbar/partials/FilterQuestions';
import SDGIcon from '../sdg-icon/sdg-icon';
import { SuggestionsProps, TargetValuesProps } from '../target-actual/TargetActualModal';
import { SelectOption } from '../../types/select';
import './style.scss';
import { getCode } from './utils';

interface CustomMaterialityProps {
  message: string;
  saving: boolean;
  handleClearMessage: () => void;
  handleUpdate: (value: string[]) => void;
  toggle: () => void;
  targetValues: TargetValuesProps;
  setTargetValues: (value: TargetValuesProps) => void
}

const CustomMateriality = (props: CustomMaterialityProps) => {
  const { handleUpdate, toggle, message, handleClearMessage, targetValues, setTargetValues } = props;

  const [utrIds, setUtrIds] = useState<string[]>([]);

  const sdgOptions = getSDGOptions();
  const sdgGoals = sdgOptions.filter(option => option.value !== null) as SelectOption<string>[];

  const handleValueSelect = (value: string) => {
    if (!utrIds.includes(value)) {
      setUtrIds([...utrIds, value])
    }
    else {
      const values = utrIds.filter(id => id !== value)
      setUtrIds(values)
    }
    handleClearMessage();
  }

  const onChangeHandle = (value?: string) => {
    setTargetValues({
      loading: true,
      suggestions: [],
      goalCode: value,
      errorMessage: ''
    })

    return apiClient.get(`universal-trackers/search?sdgGoal=${value}`)
      .then(response => {
        setTargetValues({
          loading: false,
          suggestions: response.data.data,
          goalCode: value,
          errorMessage: ''
        })
      })
      .catch(e => {
        setTargetValues({
          loading: false,
          suggestions: [],
          goalCode: undefined,
          errorMessage: e.message
        })
      });
  }

  const renderSuggestions = () => {

    if (targetValues.loading) {
      return <Fragment>
        <Loader relative={true} />
        <ul><li><span>Loading...</span></li></ul>
      </Fragment>;
    }

    if (targetValues.suggestions.length === 0) {
      return <ul><li><span>There are no SDG {targetValues.goalCode} targets available to choose from.</span></li></ul>;
    }

    const renderSelectedSdg = () => {
      const suggestionSdg = targetValues.suggestions.find((suggestion: SuggestionsProps) => getCode(suggestion.code) === targetValues.goalCode) as SuggestionsProps;
      const sdgName = suggestionSdg.name.split('-')
      return <>
        <div className='ml-2'>
          <SDGIcon code={targetValues.goalCode} width={50} />
          <span className='ml-2 strong align-self-center'>{sdgName[0]}</span>
        </div>
        <div key={suggestionSdg._id}
          className='targetCheckbox'
        >
          {getCode(suggestionSdg.code) === targetValues.goalCode &&
            <FormGroup check className='mt-3 mb-3'>
              <Input type='checkbox' className='mt-1' onChange={() => handleValueSelect(suggestionSdg._id)} checked={utrIds.includes(suggestionSdg._id)} />
              <Label check>
                {sdgName[1]}
              </Label>
            </FormGroup>
          }
        </div>
      </>
    }
    const suggestionTargets = targetValues.suggestions.filter(suggestion => getCode(suggestion.code) !== targetValues.goalCode);

    return <div className='suggestion-wrapper'>
      {renderSelectedSdg()}
      <div className='text-ThemeTextMedium'>
        <b className='ml-2'>Targets</b>
        {suggestionTargets.map((suggestion) =>
          <div key={suggestion._id}
            className='targetCheckbox'
          >
            <FormGroup check className='mt-3'>
              <Input type='checkbox' className='mt-1' onChange={(e) => handleValueSelect(suggestion._id)} checked={utrIds.includes(suggestion._id)} />
              <Label check className='mt-3'>
                {`${getCode(suggestion.code)} ${suggestion.name}`}
              </Label>
            </FormGroup>
          </div>
        )}
      </div>
    </div>
  }
  return <>
    <div className='sdg-icons'>
      <p className='mb-3'>1. Pick the SDG Goal of the targets you want to track</p>
      <FilterOption
        isClearable={true}
        placeholder='Select SDG'
        options={sdgGoals}
        value={targetValues.goalCode ? sdgOptions.find(o => o.value === targetValues.goalCode) : null}
        onChange={(o) => onChangeHandle(o?.value ?? undefined)}
      />
    </div>
    {targetValues.goalCode && <div className='cm-suggestions mr-5'>
      <p className='mb-3 strong ml-2'>2. Pick the SDG targets you want to track</p>
      {renderSuggestions()}
    </div>}
    <div className='text-right mt-5'>
      <Button color='link-secondary' className='mr-3' onClick={toggle}>Cancel</Button>
      <Button disabled={message !== '' || utrIds.length === 0} onClick={() => handleUpdate(utrIds)}>Save</Button>
    </div>
  </>
}


export default CustomMateriality;
